import React, { JSX } from 'react';
import classNames from 'classnames';
import { notNil } from '../../core/helpers/is-nil';
import styles from './typography.module.scss';

export type TypographyVariant =
  'headline-1' | 'headline-2' | 'headline-3' | 'leadtext' | 'maintext' | 'buttontext' | 'smalltext';

// context color is ought to be used when the color shall be chosen by the parent component
export type TypographyColor = 'black' | 'red' | 'white' | 'anthrazit' | 'anthrazit-75' | 'anthrazit-50';

const VARIANT_DEFAULT_TAG_MAP: Record<TypographyVariant, keyof JSX.IntrinsicElements> = {
  'headline-1': 'h1',
  'headline-2': 'h2',
  'headline-3': 'h3',
  leadtext: 'p',
  maintext: 'p',
  buttontext: 'p',
  smalltext: 'p',
};

type FontStyle = 'bold' | 'italic';

type Props = {
  variant?: TypographyVariant;
  tag?: keyof JSX.IntrinsicElements;
  color?: TypographyColor;
  fontStyle?: FontStyle;
} & React.HTMLAttributes<HTMLOrSVGElement>;

export const Typography: React.FC<Props> = ({
  tag,
  variant = 'maintext',
  color = 'black',
  fontStyle,
  style,
  className,
  ...rest
}) => {
  const Component = tag === undefined ? VARIANT_DEFAULT_TAG_MAP[variant] : tag;
  const colorClassname = `color-${color}`;

  if (notNil(fontStyle) && variant !== 'maintext' && variant !== 'smalltext') {
    // Accept the console.warn for development purposes
    // eslint-disable-next-line no-console
    console.warn('Typography component does not support fontStyle prop for this variant', variant);
  }

  let fontStyleOverwrite = fontStyle;

  if (variant === 'buttontext') {
    fontStyleOverwrite = 'bold';
  }

  const styleClassName = notNil(fontStyleOverwrite) ? styles[`style-${fontStyleOverwrite}`] : '';

  return (
    <Component
      className={classNames(
        styles.typography,
        styles[variant],
        styleClassName,
        {
          [styles[colorClassname]]: color !== 'black',
        },
        className,
      )}
      {...rest}
    />
  );
};
